import React, { useState } from 'react';
import { Box, Typography, Chip, Link, Collapse, IconButton } from '@mui/material';
import { Flag as FlagIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';

interface ExtractionOutputRendererProps {
  entityType: string;
  output: any;
  maxHeight?: number;
  onEvidenceClick?: (evidence: string) => void;
}

/**
 * Renders extraction output in entity-specific formats.
 * Used in both PromptEvaluationDetail and PromptDebugModal for consistent display.
 */
export default function ExtractionOutputRenderer({
  entityType,
  output,
  maxHeight = 400,
  onEvidenceClick,
}: ExtractionOutputRendererProps) {
  if (!output) return null;

  const items = Array.isArray(output) ? output : [output];

  // Defrauded Parties rendering
  if (entityType === 'defrauded_parties') {
    return (
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        {items.map((party: any, i: number) => (
          <Box
            key={i}
            onClick={() => party.evidence && onEvidenceClick?.(party.evidence)}
            sx={{
              mb: 1.5,
              p: 1.5,
              bgcolor: party.flag ? 'warning.50' : 'background.paper',
              border: '1px solid',
              borderColor: party.flag ? 'warning.main' : 'divider',
              borderRadius: 1,
              cursor: party.evidence && onEvidenceClick ? 'pointer' : 'default',
              '&:hover': party.evidence && onEvidenceClick ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
            }}
          >
            {/* Party Name + Source Badge */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                {party.url ? (
                  <Link
                    href={party.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'primary.main' }}
                  >
                    {party.name}
                  </Link>
                ) : (
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {party.name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                {party.flag && (
                  <Chip
                    icon={<FlagIcon sx={{ fontSize: '0.75rem !important' }} />}
                    label="needs review"
                    size="small"
                    color="warning"
                    sx={{ height: 18, fontSize: '0.6rem' }}
                  />
                )}
                <Chip
                  label={party.source_type === 'web' ? 'web' : 'press release'}
                  size="small"
                  color={party.source_type === 'web' ? 'info' : 'default'}
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
              </Box>
            </Box>

            {/* Definition */}
            {party.definition && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.75, lineHeight: 1.4 }}>
                {party.definition}
              </Typography>
            )}

            {/* Evidence */}
            {party.evidence && (
              <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                  "{party.evidence}"
                </Typography>
              </Box>
            )}

            {/* Flag Why */}
            {party.flag && party.flag_why && (
              <Box sx={{ mt: 0.75, p: 0.75, bgcolor: 'warning.100', borderRadius: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'warning.dark', lineHeight: 1.4 }}>
                  <strong>Why flagged:</strong> {party.flag_why}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // Funding Programs rendering
  if (entityType === 'funding_programs') {
    return (
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        {items.map((program: any, i: number) => (
          <Box
            key={i}
            onClick={() => program.evidence && onEvidenceClick?.(program.evidence)}
            sx={{
              mb: 1.5,
              p: 1.5,
              bgcolor: program.flag ? 'warning.50' : 'background.paper',
              border: '1px solid',
              borderColor: program.flag ? 'warning.main' : 'divider',
              borderRadius: 1,
              cursor: program.evidence && onEvidenceClick ? 'pointer' : 'default',
              '&:hover': program.evidence && onEvidenceClick ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
            }}
          >
            {/* Defrauded Party link */}
            {program.defrauded_party && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 500 }}>Q1:</Typography>
                <Chip
                  label={program.defrauded_party}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Box>
            )}

            {/* Program Name + Source Badge */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                {program.url ? (
                  <Link
                    href={program.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'primary.main' }}
                  >
                    {program.name}
                  </Link>
                ) : (
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {program.name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                {program.flag && (
                  <Chip
                    icon={<FlagIcon sx={{ fontSize: '0.75rem !important' }} />}
                    label="needs review"
                    size="small"
                    color="warning"
                    sx={{ height: 18, fontSize: '0.6rem' }}
                  />
                )}
                <Chip
                  label={program.source_type === 'web' ? 'web' : 'press release'}
                  size="small"
                  color={program.source_type === 'web' ? 'info' : 'default'}
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
              </Box>
            </Box>

            {/* Description */}
            {program.description && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, lineHeight: 1.4 }}>
                <strong>Why:</strong> {program.description}
              </Typography>
            )}

            {/* How */}
            {program.how && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, lineHeight: 1.4 }}>
                <strong>How:</strong> {program.how}
              </Typography>
            )}

            {/* Evidence */}
            {program.evidence && (
              <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                  "{program.evidence}"
                </Typography>
              </Box>
            )}

            {/* Flag Why */}
            {program.flag && program.flag_why && (
              <Box sx={{ mt: 0.75, p: 0.75, bgcolor: 'warning.100', borderRadius: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'warning.dark', lineHeight: 1.4 }}>
                  <strong>Why flagged:</strong> {program.flag_why}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // Misrep Type rendering
  if (entityType === 'misrep_type') {
    return (
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        {items.map((misrep: any, i: number) => (
          <Box
            key={i}
            onClick={() => misrep.evidence && onEvidenceClick?.(misrep.evidence)}
            sx={{
              mb: 1.5,
              p: 1.5,
              bgcolor: misrep.flag ? 'warning.50' : 'background.paper',
              border: '1px solid',
              borderColor: misrep.flag ? 'warning.main' : 'divider',
              borderRadius: 1,
              cursor: misrep.evidence && onEvidenceClick ? 'pointer' : 'default',
              '&:hover': misrep.evidence && onEvidenceClick ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
            }}
          >
            {/* Links to upstream entities */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
              {misrep.defrauded_party && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>Q1:</Typography>
                  <Chip label={misrep.defrauded_party} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                </Box>
              )}
              {misrep.funding_program && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>Q2:</Typography>
                  <Chip label={misrep.funding_program} size="small" variant="outlined" color="secondary" sx={{ height: 18, fontSize: '0.6rem' }} />
                </Box>
              )}
            </Box>

            {/* Misrep Name */}
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>
              {misrep.name}
            </Typography>

            {/* Description */}
            {misrep.description && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, lineHeight: 1.4 }}>
                {misrep.description}
              </Typography>
            )}

            {/* Evidence */}
            {misrep.evidence && (
              <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                  "{misrep.evidence}"
                </Typography>
              </Box>
            )}

            {/* Flag */}
            {misrep.flag && (
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  icon={<FlagIcon sx={{ fontSize: '0.75rem !important' }} />}
                  label="needs review"
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: '0.6rem' }}
                />
                {misrep.flag_why && (
                  <Typography sx={{ fontSize: '0.65rem', color: 'warning.dark' }}>
                    {misrep.flag_why}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // Fraud Type rendering
  if (entityType === 'fraud_type') {
    return (
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        {items.map((fraud: any, i: number) => (
          <Box
            key={i}
            onClick={() => fraud.evidence && onEvidenceClick?.(fraud.evidence)}
            sx={{
              mb: 1.5,
              p: 1.5,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              cursor: fraud.evidence && onEvidenceClick ? 'pointer' : 'default',
              '&:hover': fraud.evidence && onEvidenceClick ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
            }}
          >
            {/* Fraud Class */}
            <Chip
              label={fraud.fraud_class}
              size="small"
              color="error"
              sx={{ mb: 1, fontSize: '0.7rem' }}
            />

            {/* Misrepresentation Summary */}
            {fraud.misrepresentation_summary && (
              <Typography sx={{ fontSize: '0.8rem', mb: 0.5, lineHeight: 1.4 }}>
                {fraud.misrepresentation_summary}
              </Typography>
            )}

            {/* Evidence */}
            {fraud.evidence && (
              <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                  "{fraud.evidence}"
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // Summary (text) rendering
  if (entityType === 'summary') {
    const text = typeof output === 'string' ? output : output?.summary || JSON.stringify(output);
    return (
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {text}
        </Typography>
      </Box>
    );
  }

  // Funding Programs and Misrepresentations (Combined) - hierarchical rendering
  if (entityType === 'funding_programs_and_misrepresentations') {
    return (
      <FundingProgramsMisrepsCombined
        items={items}
        maxHeight={maxHeight}
        onEvidenceClick={onEvidenceClick}
      />
    );
  }

  // Default rendering (JSON)
  return (
    <Box sx={{ maxHeight, overflow: 'auto' }}>
      {items.map((item: any, i: number) => (
        <Box
          key={i}
          sx={{
            p: 1,
            mb: 0.5,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            {item.name || item.fraud_class || JSON.stringify(item).substring(0, 60)}
          </Typography>
          {item.definition && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {item.definition}
            </Typography>
          )}
          {item.evidence && (
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
              "{item.evidence}"
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}


/**
 * Hierarchical renderer for funding_programs_and_misrepresentations entity type.
 * Groups by defrauded party → funding programs → misrepresentations.
 */
function FundingProgramsMisrepsCombined({
  items,
  maxHeight,
  onEvidenceClick,
}: {
  items: any[];
  maxHeight: number;
  onEvidenceClick?: (evidence: string) => void;
}) {
  const [expandedParties, setExpandedParties] = useState<Record<number, boolean>>(() => {
    // Default: expand all parties
    const initial: Record<number, boolean> = {};
    items.forEach((_, i) => { initial[i] = true; });
    return initial;
  });

  const [expandedPrograms, setExpandedPrograms] = useState<Record<string, boolean>>({});

  const toggleParty = (index: number) => {
    setExpandedParties(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleProgram = (key: string) => {
    setExpandedPrograms(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Count total flags across all items
  const countFlags = (partyItem: any): number => {
    let count = 0;
    partyItem.funding_programs?.forEach((prog: any) => {
      prog.misrepresentations?.forEach((m: any) => {
        if (m.flag) count++;
      });
    });
    return count;
  };

  return (
    <Box sx={{ maxHeight, overflow: 'auto' }}>
      {items.map((partyItem: any, partyIndex: number) => {
        const flagCount = countFlags(partyItem);
        const programCount = partyItem.funding_programs?.length || 0;
        const misrepCount = partyItem.funding_programs?.reduce(
          (acc: number, p: any) => acc + (p.misrepresentations?.length || 0), 0
        ) || 0;

        return (
          <Box
            key={partyIndex}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            {/* Defrauded Party Header */}
            <Box
              onClick={() => toggleParty(partyIndex)}
              sx={{
                p: 1.5,
                bgcolor: 'primary.50',
                borderBottom: expandedParties[partyIndex] ? '1px solid' : 'none',
                borderColor: 'divider',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': { bgcolor: 'primary.100' },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'primary.dark' }}>
                  {partyItem.defrauded_party}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={`${programCount} program${programCount !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ height: 18, fontSize: '0.6rem' }}
                  />
                  <Chip
                    label={`${misrepCount} misrep${misrepCount !== 1 ? 's' : ''}`}
                    size="small"
                    color="secondary"
                    sx={{ height: 18, fontSize: '0.6rem' }}
                  />
                  {flagCount > 0 && (
                    <Chip
                      icon={<FlagIcon sx={{ fontSize: '0.7rem !important' }} />}
                      label={`${flagCount} flagged`}
                      size="small"
                      color="warning"
                      sx={{ height: 18, fontSize: '0.6rem' }}
                    />
                  )}
                </Box>
              </Box>
              <IconButton size="small">
                {expandedParties[partyIndex] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* Funding Programs */}
            <Collapse in={expandedParties[partyIndex]}>
              <Box sx={{ p: 1 }}>
                {partyItem.funding_programs?.map((program: any, progIndex: number) => {
                  const progKey = `${partyIndex}-${progIndex}`;
                  const isExpanded = expandedPrograms[progKey] !== false; // Default expanded
                  const progMisrepCount = program.misrepresentations?.length || 0;
                  const progFlagCount = program.misrepresentations?.filter((m: any) => m.flag).length || 0;

                  return (
                    <Box
                      key={progIndex}
                      sx={{
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Program Header */}
                      <Box
                        onClick={() => toggleProgram(progKey)}
                        sx={{
                          p: 1,
                          bgcolor: 'grey.50',
                          borderBottom: isExpanded ? '1px solid' : 'none',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'flex-start',
                          '&:hover': { bgcolor: 'grey.100' },
                        }}
                      >
                        <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.primary' }}>
                            {program.name}
                          </Typography>
                          {program.why_exists && (
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
                              <strong>Why:</strong> {program.why_exists}
                            </Typography>
                          )}
                          {program.how_delivers_funds && (
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
                              <strong>How:</strong> {program.how_delivers_funds}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            <Chip
                              label={`${progMisrepCount} misrep${progMisrepCount !== 1 ? 's' : ''}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.55rem' }}
                            />
                            {progFlagCount > 0 && (
                              <Chip
                                icon={<FlagIcon sx={{ fontSize: '0.65rem !important' }} />}
                                label={`${progFlagCount}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.55rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Misrepresentations */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: 1, bgcolor: 'background.paper' }}>
                          {program.misrepresentations?.map((misrep: any, misrepIndex: number) => (
                            <Box
                              key={misrepIndex}
                              onClick={() => misrep.evidence && onEvidenceClick?.(misrep.evidence)}
                              sx={{
                                mb: 1,
                                p: 1,
                                bgcolor: misrep.flag ? 'warning.50' : 'grey.50',
                                border: '1px solid',
                                borderColor: misrep.flag ? 'warning.main' : 'grey.200',
                                borderRadius: 0.5,
                                cursor: misrep.evidence && onEvidenceClick ? 'pointer' : 'default',
                                '&:hover': misrep.evidence && onEvidenceClick ? { borderColor: 'primary.main' } : {},
                                '&:last-child': { mb: 0 },
                              }}
                            >
                              {/* Misrep Header */}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  {misrep.url ? (
                                    <Link
                                      href={misrep.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'primary.main' }}
                                    >
                                      {misrep.name}
                                    </Link>
                                  ) : (
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                      {misrep.name}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                  {misrep.flag && (
                                    <Chip
                                      icon={<FlagIcon sx={{ fontSize: '0.65rem !important' }} />}
                                      label="review"
                                      size="small"
                                      color="warning"
                                      sx={{ height: 16, fontSize: '0.55rem' }}
                                    />
                                  )}
                                  <Chip
                                    label={misrep.source_type === 'press_release' ? 'PR' : (misrep.source_type || 'PR')}
                                    size="small"
                                    color={misrep.source_type === 'web' ? 'info' : 'default'}
                                    sx={{ height: 16, fontSize: '0.55rem' }}
                                  />
                                </Box>
                              </Box>

                              {/* Description */}
                              {misrep.description && (
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.4 }}>
                                  {misrep.description}
                                </Typography>
                              )}

                              {/* Evidence */}
                              {misrep.evidence && (
                                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.3 }}>
                                    "{misrep.evidence}"
                                  </Typography>
                                </Box>
                              )}

                              {/* Flag Why */}
                              {misrep.flag && misrep.flag_why && (
                                <Box sx={{ mt: 0.5, p: 0.5, bgcolor: 'warning.100', borderRadius: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.6rem', color: 'warning.dark', lineHeight: 1.3 }}>
                                    <strong>⚠️ Why flagged:</strong> {misrep.flag_why}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                          {(!program.misrepresentations || program.misrepresentations.length === 0) && (
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontStyle: 'italic' }}>
                              No misrepresentations identified
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
                {(!partyItem.funding_programs || partyItem.funding_programs.length === 0) && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic', p: 1 }}>
                    No funding programs identified
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
